import React from "react";
import "../../styles/shared/ActionSwitchPill.css";

type SwitchSide = "left" | "right";

interface ActionSwitchPillProps {
  leftLabel: string;
  rightLabel: string;
  activeSide: SwitchSide;
  onClick?: () => void;
  disabled?: boolean;
  interactive?: boolean;
  className?: string;
  caption?: string;
  showCaption?: boolean;
  ariaLabel?: string;
  tone?: "service" | "delivery" | "neutral";
}

const ActionSwitchPill: React.FC<ActionSwitchPillProps> = ({
  leftLabel,
  rightLabel,
  activeSide,
  onClick,
  disabled = false,
  interactive = true,
  className = "",
  caption,
  showCaption = true,
  ariaLabel,
  tone = "service",
}) => {
  const activeLabel = activeSide === "right" ? rightLabel : leftLabel;
  const buttonClassName = [
    "action-switch-pill",
    `action-switch-pill--${tone}`,
    activeSide === "right" ? "is-right" : "is-left",
    disabled ? "is-disabled" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      <span className="action-switch-pill__rail" aria-hidden="true">
        <span className="action-switch-pill__thumb" />
        <span className="action-switch-pill__label action-switch-pill__label--left">{leftLabel}</span>
        <span className="action-switch-pill__label action-switch-pill__label--right">{rightLabel}</span>
      </span>
      {showCaption ? (
        <span className="action-switch-pill__caption">
          {caption ? caption : <>Active: <strong>{activeLabel}</strong></>}
        </span>
      ) : null}
    </>
  );

  if (!interactive) {
    return (
      <div
        className={`${buttonClassName} is-display-only`}
        role="status"
        aria-label={ariaLabel || `${leftLabel} or ${rightLabel}`}
      >
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={buttonClassName}
      onClick={onClick}
      disabled={disabled || !onClick}
      aria-label={ariaLabel || `${leftLabel} or ${rightLabel}`}
      aria-pressed={activeSide === "right"}
    >
      {content}
    </button>
  );
};

export default ActionSwitchPill;
